import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, ShoppingBag, DollarSign, TrendingUp, Plus, X, 
  Edit2, Trash2, Eye, EyeOff, Upload, Image, Save, ChevronDown,
  LogOut, Home, Settings, LayoutDashboard, Truck, Check, Clock,
  Tag, Percent, Calendar, Copy, ToggleLeft, ToggleRight, BarChart3,
  Search, MapPin, ExternalLink, AlertCircle, CheckCircle, Star
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Track Order Section Component
const TrackOrderSection = ({ token }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) {
      setError('Please enter both order number and email');
      return;
    }
    
    setLoading(true);
    setError('');
    setOrder(null);
    
    try {
      const response = await axios.post(`${API}/track-order`, {
        order_number: orderNumber.trim(),
        email: email.trim().toLowerCase()
      });
      setOrder(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found. Please check your details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
      case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing':
      case 'confirmed': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'shipped': return <Truck className="w-5 h-5 text-blue-600" />;
      case 'processing':
      case 'confirmed': return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'cancelled': return <AlertCircle className="w-5 h-5 text-red-600" />;
      default: return <Package className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    if (!carrier || !trackingNumber) return null;
    const c = carrier.toLowerCase();
    if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`;
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statusSteps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
  const currentStepIndex = order ? statusSteps.indexOf(order.status?.toLowerCase()) : -1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">Track Order</h2>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                placeholder="e.g., NP2024XXXXXX"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent font-mono"
                data-testid="admin-order-number-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                data-testid="admin-email-input"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-[#D4AF37] text-white px-6 py-2 rounded-lg hover:bg-[#B5952F] transition-colors font-medium flex items-center gap-2 disabled:bg-gray-300"
            data-testid="admin-track-button"
          >
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Searching...</>
            ) : (
              <><Search className="w-4 h-4" /> Track Order</>
            )}
          </button>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Order Results */}
      {order && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid="admin-order-results">
          {/* Order Header */}
          <div className="bg-[#1A1A1A] text-white p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-gray-400 mb-1">Order Number</p>
                <p className="text-xl font-mono font-bold">{order.order_number}</p>
              </div>
              <div className={`px-4 py-2 rounded-full border ${getStatusColor(order.status)} flex items-center gap-2`}>
                {getStatusIcon(order.status)}
                <span className="font-medium capitalize">{order.status}</span>
              </div>
            </div>
          </div>

          {/* Progress Tracker */}
          {order.status !== 'cancelled' && (
            <div className="p-6 border-b">
              <div className="flex items-center justify-between relative">
                <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 z-0">
                  <div
                    className="h-full bg-[#D4AF37] transition-all duration-500"
                    style={{ width: `${Math.max(0, (currentStepIndex / (statusSteps.length - 1)) * 100)}%` }}
                  />
                </div>
                {statusSteps.map((step, index) => (
                  <div key={step} className="flex flex-col items-center relative z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      index <= currentStepIndex ? 'bg-[#D4AF37] text-white' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {index < currentStepIndex ? <Check className="w-5 h-5" /> : <span className="text-sm font-bold">{index + 1}</span>}
                    </div>
                    <span className={`text-xs mt-2 capitalize ${index <= currentStepIndex ? 'text-[#D4AF37] font-medium' : 'text-gray-400'}`}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tracking Info */}
          {order.tracking_number && (
            <div className="p-6 border-b bg-blue-50">
              <div className="flex items-center gap-3 mb-3">
                <Truck className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-900">Shipment Tracking</h3>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Tracking Number</p>
                    <p className="font-mono font-bold text-lg">{order.tracking_number}</p>
                    {order.carrier && <p className="text-sm text-gray-600">{order.carrier}</p>}
                  </div>
                  {getTrackingUrl(order.carrier, order.tracking_number) && (
                    <a
                      href={getTrackingUrl(order.carrier, order.tracking_number)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" /> Track Package
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Order Details */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Items */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Order Items
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.product_name}</span>
                      <span className="text-gray-500">x{item.quantity}</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                    <span>Total</span>
                    <span>${order.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Shipping */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Shipping To
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 text-sm">
                  <p>{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.zip_code}</p>
                  <p className="text-gray-500 mt-2 capitalize">
                    Method: {order.shipping_method === 'pickup' ? 'Local Pickup' : 'Delivery'}
                  </p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="mt-6 pt-6 border-t text-sm text-gray-500 flex flex-wrap gap-6">
              <div><span className="font-medium text-gray-700">Ordered:</span> {formatDate(order.created_at)}</div>
              {order.updated_at && order.updated_at !== order.created_at && (
                <div><span className="font-medium text-gray-700">Last Update:</span> {formatDate(order.updated_at)}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, token, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // US Insurance state
  const [usinsureEmails, setUsinsureEmails] = useState([]);
  const [usinusreCodes, setUsinsureCodes] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [usinsureLoading, setUsinsureLoading] = useState(false);

  // Order Management state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState('all');
  const [orderStats, setOrderStats] = useState(null);
  const [updatingOrder, setUpdatingOrder] = useState(false);

  // Promo Code state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_order_value: '',
    max_uses: '',
    expires_at: '',
    description: ''
  });

  // Reports state
  const [reportsSummary, setReportsSummary] = useState(null);
  const [salesChartData, setSalesChartData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  // Reviews state
  const [adminReviews, setAdminReviews] = useState([]);
  const [reviewsStats, setReviewsStats] = useState({ pending: 0, approved: 0, total: 0 });
  const [reviewFilter, setReviewFilter] = useState('all');
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Helper function for tracking URLs
  const getTrackingUrl = (carrier, trackingNumber) => {
    if (!carrier || !trackingNumber) return null;
    const c = carrier.toLowerCase();
    if (c.includes('usps')) return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    if (c.includes('ups')) return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    if (c.includes('fedex')) return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
    if (c.includes('dhl')) return `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${trackingNumber}`;
    return null;
  };

  const categories = ['Clothing', 'Traditional Wear', 'Jewelry', 'Accessories', 'Beauty & Cosmetics', 'Home & Living'];
  
  // Subcategories based on main category
  const subcategoryOptions = {
    'Clothing': {
      gender: ['', 'women', 'men', 'kids'],
      type: ['', 'casual', 'formal', 'seasonal', 'ethnic']
    },
    'Traditional Wear': {
      gender: ['', 'women', 'men', 'kids'],
      type: ['', 'saree', 'lehenga', 'kurtha', 'sherpa', 'tamang', 'newari', 'gurung', 'daura-suruwal', 'cultural']
    },
    'Jewelry': {
      gender: ['', 'women', 'men', 'unisex'],
      type: ['', 'necklace', 'earrings', 'bangles', 'rings', 'bracelets', 'anklets', 'sets']
    },
    'Accessories': {
      gender: ['', 'women', 'men', 'kids', 'unisex'],
      type: ['', 'bags', 'scarves', 'shawls', 'hats', 'hair-accessories', 'belts', 'wallets']
    },
    'Beauty & Cosmetics': {
      gender: ['', 'women', 'men', 'unisex'],
      type: ['', 'skincare', 'haircare', 'makeup', 'fragrance', 'ayurvedic', 'natural']
    },
    'Home & Living': {
      gender: [],
      type: ['', 'decor', 'textiles', 'spiritual', 'kitchenware', 'stationery', 'gifts']
    }
  };

  const defaultSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
  const defaultColors = ['Black', 'White', 'Red', 'Blue', 'Gold', 'Multi-color'];

  const emptyProduct = {
    name: '',
    description: '',
    category: 'Clothing',
    subcategory: '',
    base_price: '',
    compare_at_price: '',
    images: [],
    show_online: true,
    is_featured: false,
    is_new_arrival: true,
    is_nepora_original: false,
    pickup_today: true,
    gender: '',
    clothing_type: '',
    traditional_type: '',
    size_guide: '',
    care_instructions: '',
    tags: [],
    variants: [{ size: 'M', color: 'Multi-color', stock: 10, sku: '' }]
  };

  const [productForm, setProductForm] = useState(emptyProduct);
  const [imageUrls, setImageUrls] = useState(['']);
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // Redirect non-admin users to admin login
    if (!user?.is_admin) {
      navigate('/admin/login');
      return;
    }
    
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ordersRes, productsRes, statsRes, emailsRes, codesRes, promoRes, orderStatsRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products?limit=100`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reports/sales?period=weekly`, { headers }).catch(() => ({ data: { total_sales: 0, total_orders: 0, average_order_value: 0 } })),
        axios.get(`${API}/admin/usinsure/emails`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/usinsure/codes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/promo-codes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders/stats/summary`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
      setStats(statsRes.data);
      setUsinsureEmails(emailsRes.data || []);
      setUsinsureCodes(codesRes.data || []);
      setPromoCodes(promoRes.data || []);
      setOrderStats(orderStatsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch admin data', error);
    } finally {
      setLoading(false);
    }
  };

  // US Insurance functions
  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    
    setUsinsureLoading(true);
    try {
      await axios.post(`${API}/admin/usinsure/emails`, { email: newEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `${newEmail} added to approved list` });
      setNewEmail('');
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add email' });
    } finally {
      setUsinsureLoading(false);
    }
  };

  const handleRemoveEmail = async (email) => {
    if (!confirm(`Remove ${email} from approved list?`)) return;
    
    try {
      await axios.delete(`${API}/admin/usinsure/emails/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `${email} removed from approved list` });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove email' });
    }
  };

  const handleBulkAdd = async () => {
    const emails = bulkEmails.split(/[\n,;]/).map(e => e.trim()).filter(e => e);
    if (emails.length === 0) return;
    
    setUsinsureLoading(true);
    try {
      const res = await axios.post(`${API}/admin/usinsure/emails/bulk`, { emails }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: res.data.message });
      setBulkEmails('');
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to add emails' });
    } finally {
      setUsinsureLoading(false);
    }
  };

  // Order Management Functions
  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Order status updated to ${newStatus}` });
      fetchData();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update order status' });
    } finally {
      setUpdatingOrder(false);
    }
  };

  const updateOrderTracking = async (orderId, trackingNumber, carrier) => {
    setUpdatingOrder(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/tracking`, { tracking_number: trackingNumber, carrier }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Tracking information updated' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update tracking' });
    } finally {
      setUpdatingOrder(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (orderFilter === 'all') return true;
    return order.status === orderFilter;
  });

  // Promo Code Functions
  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...promoForm,
        discount_value: parseFloat(promoForm.discount_value) || 0,
        min_order_value: promoForm.min_order_value ? parseFloat(promoForm.min_order_value) : null,
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
        expires_at: promoForm.expires_at || null
      };

      if (editingPromo) {
        await axios.put(`${API}/admin/promo-codes/${editingPromo.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Promo code updated successfully' });
      } else {
        await axios.post(`${API}/admin/promo-codes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: `Promo code ${data.code} created` });
      }
      
      setShowAddPromo(false);
      setEditingPromo(null);
      setPromoForm({ code: '', discount_type: 'percentage', discount_value: '', min_order_value: '', max_uses: '', expires_at: '', description: '' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save promo code' });
    } finally {
      setSaving(false);
    }
  };

  const togglePromoCode = async (promoId) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promoId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle promo code' });
    }
  };

  const deletePromoCode = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Promo code deleted' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete promo code' });
    }
  };

  const editPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      code: promo.code,
      discount_type: promo.discount_type || 'percentage',
      discount_value: promo.discount_value?.toString() || '',
      min_order_value: promo.min_order_value?.toString() || '',
      max_uses: promo.max_uses?.toString() || '',
      expires_at: promo.expires_at ? promo.expires_at.split('T')[0] : '',
      description: promo.description || ''
    });
    setShowAddPromo(true);
  };

  // Fetch Reports Data
  const fetchReportsData = async () => {
    setReportsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [summaryRes, chartRes, topRes, categoryRes] = await Promise.all([
        axios.get(`${API}/admin/reports/summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/reports/sales-chart?days=30`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reports/top-products?limit=10`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reports/category-breakdown`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setReportsSummary(summaryRes.data);
      setSalesChartData(chartRes.data || []);
      setTopProducts(topRes.data || []);
      setCategoryBreakdown(categoryRes.data || []);
    } catch (error) {
      console.error('Failed to fetch reports', error);
    } finally {
      setReportsLoading(false);
    }
  };

  // Fetch reports when tab changes to reports
  useEffect(() => {
    if (activeTab === 'reports' && token) {
      fetchReportsData();
    }
    if (activeTab === 'reviews' && token) {
      fetchAdminReviews();
    }
  }, [activeTab, token]);

  // Fetch admin reviews
  const fetchAdminReviews = async () => {
    setReviewsLoading(true);
    try {
      const status = reviewFilter === 'all' ? '' : reviewFilter;
      const response = await axios.get(`${API}/admin/reviews${status ? `?status=${status}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminReviews(response.data.reviews || []);
      setReviewsStats(response.data.stats || { pending: 0, approved: 0, total: 0 });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewAction = async (reviewId, action) => {
    try {
      if (action === 'approve') {
        await axios.put(`${API}/admin/reviews/${reviewId}`, { approved: true }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review approved');
      } else if (action === 'reject') {
        await axios.delete(`${API}/admin/reviews/${reviewId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review deleted');
      }
      fetchAdminReviews();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...productForm.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setProductForm(prev => ({ ...prev, variants: newVariants }));
  };

  const addVariant = () => {
    setProductForm(prev => ({
      ...prev,
      variants: [...prev.variants, { size: 'M', color: 'Multi-color', stock: 10, sku: '' }]
    }));
  };

  const removeVariant = (index) => {
    setProductForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const addImageUrl = () => {
    setImageUrls(prev => [...prev, '']);
  };

  const removeImageUrl = (index) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const removeUploadedImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setUploadingImages(true);
    const newImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        // Convert to base64 for preview (in production, upload to cloud storage)
        const reader = new FileReader();
        const imageUrl = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        newImages.push(imageUrl);
      }
    }
    
    setUploadedImages(prev => [...prev, ...newImages]);
    setUploadingImages(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      // Combine URL images and uploaded images
      const urlImages = imageUrls.filter(url => url.trim() !== '');
      const allImages = [...urlImages, ...uploadedImages];
      
      const productData = {
        ...productForm,
        base_price: parseFloat(productForm.base_price) || 0,
        compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null,
        retail_price: parseFloat(productForm.base_price) || 0,
        images: allImages,
        variants: productForm.variants.map(v => ({
          ...v,
          stock: parseInt(v.stock) || 0
        }))
      };

      const headers = { Authorization: `Bearer ${token}` };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers });
        setMessage({ type: 'success', text: 'Product updated successfully!' });
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers });
        setMessage({ type: 'success', text: 'Product added successfully!' });
      }

      // Reset form and refresh data
      setProductForm(emptyProduct);
      setImageUrls(['']);
      setUploadedImages([]);
      setShowAddProduct(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save product', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save product' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'Clothing',
      subcategory: product.subcategory || '',
      base_price: product.base_price?.toString() || '',
      compare_at_price: product.compare_at_price?.toString() || '',
      images: product.images || [],
      show_online: product.show_online ?? true,
      is_featured: product.is_featured ?? false,
      is_new_arrival: product.is_new_arrival ?? false,
      is_nepora_original: product.is_nepora_original ?? false,
      pickup_today: product.pickup_today ?? true,
      gender: product.gender || '',
      clothing_type: product.clothing_type || '',
      traditional_type: product.traditional_type || '',
      size_guide: product.size_guide || '',
      care_instructions: product.care_instructions || '',
      tags: product.tags || [],
      variants: product.variants?.length > 0 ? product.variants : [{ size: 'M', color: 'Multi-color', stock: 10, sku: '' }]
    });
    setImageUrls(product.images?.length > 0 ? product.images : ['']);
    setUploadedImages([]);
    setShowAddProduct(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Product deleted successfully!' });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete product' });
    }
  };

  const toggleProductVisibility = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, {
        ...product,
        show_online: !product.show_online
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update product' });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Admin Top Bar */}
      <div className="bg-[#1A1A1A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nepora-shop/artifacts/pnjbs1wu_ChatGPT%20Image%20Feb%2026%2C%202026%2C%2012_48_27%20PM.png" 
                  alt="Nepora" 
                  className="h-10 w-auto"
                />
              </Link>
              <span className="hidden sm:inline text-white/50">|</span>
              <span className="hidden sm:inline text-sm font-medium">Admin Portal</span>
            </div>

            {/* Admin Actions */}
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">View Store</span>
              </Link>
              <div className="flex items-center gap-3 pl-4 border-l border-white/20">
                <div className="hidden sm:block text-right">
                  <p className="text-xs text-white/50">Logged in as</p>
                  <p className="text-sm font-medium">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  title="Sign Out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-title">
              Dashboard
            </h1>
            <p className="text-gray-600">Manage your store products and orders</p>
          </div>
          <button
            onClick={() => { setShowAddProduct(true); setEditingProduct(null); setProductForm(emptyProduct); setImageUrls(['']); setUploadedImages([]); }}
            className="mt-4 md:mt-0 inline-flex items-center gap-2 bg-[#D4AF37] text-white px-6 py-3 font-medium hover:bg-[#B5952F] transition-colors"
            data-testid="add-product-button"
          >
            <Plus className="w-5 h-5" />
            Add New Product
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-200 overflow-x-auto">
          {['overview', 'products', 'orders', 'reviews', 'track-order', 'promo-codes', 'reports', 'usinsure'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'usinsure' ? 'US Insurance' : tab === 'promo-codes' ? 'Promo Codes' : tab === 'track-order' ? 'Track Order' : tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm" data-testid="stat-sales">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Sales (7 days)</p>
                      <p className="text-2xl font-bold">${stats.total_sales?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm" data-testid="stat-orders">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Orders</p>
                      <p className="text-2xl font-bold">{stats.total_orders || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Products</p>
                      <p className="text-2xl font-bold">{products.length}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg border border-gray-100 shadow-sm" data-testid="stat-avg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Order Value</p>
                      <p className="text-2xl font-bold">${stats.average_order_value?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#FDF5E6] rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Views */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Recent Products */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold">Recent Products</h2>
                  <button onClick={() => setActiveTab('products')} className="text-sm text-[#D4AF37] hover:underline">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {products.slice(0, 5).map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
                        {product.images?.[0] ? (
                          <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.category}</p>
                      </div>
                      <p className="font-bold text-sm">${product.base_price?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold">Recent Orders</h2>
                  <button onClick={() => setActiveTab('orders')} className="text-sm text-[#D4AF37] hover:underline">
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {orders.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4 text-center">No orders yet</p>
                  ) : (
                    orders.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">#{order.order_number}</p>
                          <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm">${order.total?.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            order.status === 'completed' ? 'bg-green-100 text-green-700' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">All Products ({products.length})</h2>
                <button
                  onClick={() => { setShowAddProduct(true); setEditingProduct(null); setProductForm(emptyProduct); setImageUrls(['']); }}
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-white px-4 py-2 text-sm font-medium hover:bg-[#B5952F] transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              </div>
            </div>
            
            {/* Product Table */}
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="products-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50" data-testid={`product-row-${product.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                            {product.images?.[0] ? (
                              <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            <p className="text-xs text-gray-500">{product.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{product.category}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-sm">${product.base_price?.toFixed(2)}</p>
                          {product.compare_at_price && (
                            <p className="text-xs text-gray-400 line-through">${product.compare_at_price.toFixed(2)}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {product.variants?.reduce((acc, v) => acc + (v.stock || 0), 0) || 0}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          product.show_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {product.show_online ? 'Online' : 'Hidden'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleProductVisibility(product)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title={product.show_online ? 'Hide product' : 'Show product'}
                          >
                            {product.show_online ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEdit(product)}
                            className="p-2 text-gray-400 hover:text-[#D4AF37] hover:bg-gray-100 rounded"
                            title="Edit product"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                            title="Delete product"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Order Stats */}
            {orderStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                  <p className="text-2xl font-bold">{orderStats.total_orders || 0}</p>
                  <p className="text-xs text-gray-500 uppercase">Total</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg shadow-sm text-center border border-yellow-200">
                  <p className="text-2xl font-bold text-yellow-700">{orderStats.pending_orders || 0}</p>
                  <p className="text-xs text-yellow-600 uppercase">Pending</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg shadow-sm text-center border border-blue-200">
                  <p className="text-2xl font-bold text-blue-700">{orderStats.processing_orders || 0}</p>
                  <p className="text-xs text-blue-600 uppercase">Processing</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg shadow-sm text-center border border-purple-200">
                  <p className="text-2xl font-bold text-purple-700">{orderStats.shipped_orders || 0}</p>
                  <p className="text-xs text-purple-600 uppercase">Shipped</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg shadow-sm text-center border border-green-200">
                  <p className="text-2xl font-bold text-green-700">{orderStats.delivered_orders || 0}</p>
                  <p className="text-xs text-green-600 uppercase">Delivered</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg shadow-sm text-center border border-red-200">
                  <p className="text-2xl font-bold text-red-700">{orderStats.cancelled_orders || 0}</p>
                  <p className="text-xs text-red-600 uppercase">Cancelled</p>
                </div>
                <div className="bg-[#FDF5E6] p-4 rounded-lg shadow-sm text-center border border-[#D4AF37]/30">
                  <p className="text-2xl font-bold text-[#B5952F]">${orderStats.total_revenue?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-[#D4AF37] uppercase">Revenue</p>
                </div>
              </div>
            )}

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {['all', 'pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setOrderFilter(status)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    orderFilter === status
                      ? 'bg-[#D4AF37] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold">
                  {orderFilter === 'all' ? 'All Orders' : `${orderFilter.charAt(0).toUpperCase() + orderFilter.slice(1)} Orders`} ({filteredOrders.length})
                </h2>
              </div>
              
              {filteredOrders.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  No orders found. {orderFilter !== 'all' && 'Try changing the filter.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="orders-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracking</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-medium text-sm">#{order.order_number}</p>
                            <p className="text-xs text-gray-400">{order.id?.slice(0, 8)}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm font-medium">{order.shipping_address?.first_name} {order.shipping_address?.last_name}</p>
                            <p className="text-xs text-gray-500">{order.shipping_address?.email}</p>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {order.items?.length || 0} item(s)
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-medium text-sm">${order.total?.toFixed(2)}</p>
                            {order.discount_amount > 0 && (
                              <p className="text-xs text-green-600">-${order.discount_amount?.toFixed(2)} off</p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                              disabled={updatingOrder}
                              className={`text-xs font-medium px-2 py-1 rounded border-0 cursor-pointer ${
                                order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                                order.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                                order.status === 'confirmed' ? 'bg-cyan-100 text-cyan-700' :
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="processing">Processing</option>
                              <option value="shipped">Shipped</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                              <option value="refunded">Refunded</option>
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            {order.tracking_number ? (
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1">
                                  <Truck className="w-3 h-3 text-green-600" />
                                  <span className="text-xs font-mono">{order.tracking_number.slice(0, 12)}...</span>
                                </div>
                                {order.carrier && (
                                  <a
                                    href={getTrackingUrl(order.carrier, order.tracking_number)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                                  >
                                    Track
                                  </a>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">No tracking</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="text-xs text-[#D4AF37] hover:underline font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Order Detail Modal */}
            {selectedOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
                    <div>
                      <h2 className="text-lg font-bold">Order #{selectedOrder.order_number}</h2>
                      <p className="text-sm text-gray-500">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {/* Customer Info */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Customer</h3>
                        <p className="font-medium">{selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}</p>
                        <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.email}</p>
                        <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.phone}</p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Shipping Address</h3>
                        <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.address}</p>
                        <p className="text-sm text-gray-600">{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.zip_code}</p>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">Items</h3>
                      <div className="border rounded-lg divide-y">
                        {selectedOrder.items?.map((item, idx) => (
                          <div key={idx} className="p-3 flex justify-between items-center">
                            <div>
                              <p className="font-medium text-sm">{item.product_name}</p>
                              {item.variant_info && (
                                <p className="text-xs text-gray-500">
                                  {item.variant_info.size && `Size: ${item.variant_info.size}`}
                                  {item.variant_info.color && ` • Color: ${item.variant_info.color}`}
                                </p>
                              )}
                              <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                            </div>
                            <p className="font-medium">${item.subtotal?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Subtotal</span>
                          <span>${selectedOrder.subtotal?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Shipping ({selectedOrder.shipping_method})</span>
                          <span>${selectedOrder.shipping_cost?.toFixed(2)}</span>
                        </div>
                        {selectedOrder.discount_amount > 0 && (
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({selectedOrder.discount_code})</span>
                            <span>-${selectedOrder.discount_amount?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Tax</span>
                          <span>${selectedOrder.tax?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                          <span>Total</span>
                          <span>${selectedOrder.total?.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Update Status & Tracking */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
                        <select
                          value={selectedOrder.status}
                          onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="refunded">Refunded</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Shipping Carrier</label>
                        <select
                          defaultValue={selectedOrder.carrier || ''}
                          id="carrier-select"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                        >
                          <option value="">Select Carrier</option>
                          <option value="USPS">USPS</option>
                          <option value="UPS">UPS</option>
                          <option value="FedEx">FedEx</option>
                          <option value="DHL">DHL</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tracking Number</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            defaultValue={selectedOrder.tracking_number || ''}
                            placeholder="Enter tracking number"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                            id="tracking-input"
                          />
                          <button
                            onClick={() => {
                              const trackingInput = document.getElementById('tracking-input');
                              const carrierSelect = document.getElementById('carrier-select');
                              updateOrderTracking(selectedOrder.id, trackingInput.value, carrierSelect.value);
                            }}
                            className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B5952F]"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Track Order Tab */}
        {activeTab === 'track-order' && (
          <TrackOrderSection token={token} />
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-6">
            {/* Reviews Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-yellow-50 p-4 rounded-lg shadow-sm text-center border border-yellow-200">
                <p className="text-2xl font-bold text-yellow-700">{reviewsStats.pending}</p>
                <p className="text-xs text-yellow-600 uppercase">Pending Approval</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg shadow-sm text-center border border-green-200">
                <p className="text-2xl font-bold text-green-700">{reviewsStats.approved}</p>
                <p className="text-xs text-green-600 uppercase">Approved</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                <p className="text-2xl font-bold">{reviewsStats.total}</p>
                <p className="text-xs text-gray-500 uppercase">Total Reviews</p>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'approved'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => {
                    setReviewFilter(filter);
                    setTimeout(fetchAdminReviews, 100);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    reviewFilter === filter
                      ? 'bg-[#D4AF37] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>

            {/* Reviews List */}
            {reviewsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
              </div>
            ) : adminReviews.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <Star className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">No reviews to display</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Review</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {adminReviews.map((review) => (
                      <tr key={review.id}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {review.product_image && (
                              <img src={review.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                            )}
                            <span className="text-sm font-medium truncate max-w-[150px]">{review.product_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium">{review.reviewer_name}</p>
                            <p className="text-xs text-gray-500">{review.reviewer_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[200px]">
                            {review.title && <p className="text-sm font-medium truncate">{review.title}</p>}
                            <p className="text-sm text-gray-600 truncate">{review.review_text}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            review.approved 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {review.approved ? 'Approved' : 'Pending'}
                          </span>
                          {review.verified_purchase && (
                            <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                              Verified
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            {!review.approved && (
                              <button
                                onClick={() => handleReviewAction(review.id, 'approve')}
                                className="text-green-600 hover:text-green-800 text-sm font-medium"
                              >
                                Approve
                              </button>
                            )}
                            <button
                              onClick={() => handleReviewAction(review.id, 'reject')}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promo-codes' && (
          <div className="space-y-6">
            {/* Add Promo Code Button */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Promo Codes ({promoCodes.length})</h2>
              <button
                onClick={() => { setShowAddPromo(true); setEditingPromo(null); setPromoForm({ code: '', discount_type: 'percentage', discount_value: '', min_order_value: '', max_uses: '', expires_at: '', description: '' }); }}
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-white px-4 py-2 font-medium hover:bg-[#B5952F] transition-colors rounded-lg"
              >
                <Plus className="w-4 h-4" />
                Create Promo Code
              </button>
            </div>

            {/* Promo Codes Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {promoCodes.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  No promo codes yet. Create your first promo code to get started.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="promo-codes-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Order</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {promoCodes.map((promo) => (
                        <tr key={promo.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-[#D4AF37]">{promo.code}</span>
                              <button
                                onClick={() => { navigator.clipboard.writeText(promo.code); setMessage({ type: 'success', text: 'Code copied!' }); }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Copy code"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                            {promo.description && (
                              <p className="text-xs text-gray-500 mt-1">{promo.description}</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                              {promo.discount_type === 'percentage' ? (
                                <><Percent className="w-3 h-3" /> {promo.discount_value}%</>
                              ) : (
                                <><DollarSign className="w-3 h-3" /> {promo.discount_value}</>
                              )}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {promo.min_order_value ? `$${promo.min_order_value}` : 'None'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span className="text-gray-600">{promo.current_uses || 0}</span>
                            <span className="text-gray-400"> / {promo.max_uses || '∞'}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {promo.expires_at ? (
                              <span className={new Date(promo.expires_at) < new Date() ? 'text-red-600' : ''}>
                                {new Date(promo.expires_at).toLocaleDateString()}
                              </span>
                            ) : (
                              'Never'
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => togglePromoCode(promo.id)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                promo.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {promo.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                              {promo.active ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => editPromo(promo)}
                                className="p-2 text-gray-400 hover:text-[#D4AF37] hover:bg-gray-100 rounded"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deletePromoCode(promo.id)}
                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-gray-100 rounded"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Add/Edit Promo Code Modal */}
            {showAddPromo && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-md w-full">
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-lg font-bold">{editingPromo ? 'Edit Promo Code' : 'Create Promo Code'}</h2>
                    <button onClick={() => { setShowAddPromo(false); setEditingPromo(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <form onSubmit={handlePromoSubmit} className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code *</label>
                      <input
                        type="text"
                        value={promoForm.code}
                        onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                        placeholder="SAVE20"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] font-mono"
                        required
                        disabled={editingPromo}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                        <select
                          value={promoForm.discount_type}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_type: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount ($)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount Value *</label>
                        <input
                          type="number"
                          value={promoForm.discount_value}
                          onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })}
                          placeholder={promoForm.discount_type === 'percentage' ? '20' : '10.00'}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                          required
                          min="0"
                          step={promoForm.discount_type === 'percentage' ? '1' : '0.01'}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Value ($)</label>
                        <input
                          type="number"
                          value={promoForm.min_order_value}
                          onChange={(e) => setPromoForm({ ...promoForm, min_order_value: e.target.value })}
                          placeholder="50.00"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                        <input
                          type="number"
                          value={promoForm.max_uses}
                          onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                          placeholder="Unlimited"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                          min="1"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                      <input
                        type="date"
                        value={promoForm.expires_at}
                        onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        type="text"
                        value={promoForm.description}
                        onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
                        placeholder="Summer sale discount"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => { setShowAddPromo(false); setEditingPromo(null); }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="flex-1 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B5952F] disabled:bg-gray-300"
                      >
                        {saving ? 'Saving...' : (editingPromo ? 'Update' : 'Create')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {reportsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                {reportsSummary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">Today</p>
                      <p className="text-2xl font-bold text-[#D4AF37]">${reportsSummary.today?.revenue?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400">{reportsSummary.today?.orders || 0} orders</p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">This Week</p>
                      <p className="text-2xl font-bold text-blue-600">${reportsSummary.week?.revenue?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400">{reportsSummary.week?.orders || 0} orders</p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">This Month</p>
                      <p className="text-2xl font-bold text-green-600">${reportsSummary.month?.revenue?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400">{reportsSummary.month?.orders || 0} orders</p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <p className="text-sm text-gray-500 uppercase tracking-wide">All Time</p>
                      <p className="text-2xl font-bold">${reportsSummary.all_time?.revenue?.toFixed(2) || '0.00'}</p>
                      <p className="text-xs text-gray-400">{reportsSummary.all_time?.orders || 0} orders</p>
                    </div>
                  </div>
                )}

                {/* Sales Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-bold mb-4">Sales Trend (Last 30 Days)</h3>
                  {salesChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={salesChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                          labelFormatter={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="sales" 
                          stroke="#D4AF37" 
                          strokeWidth={2}
                          dot={{ fill: '#D4AF37', strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 6 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-400">
                      No sales data yet. Sales will appear here once orders are placed.
                    </div>
                  )}
                </div>

                {/* Top Products & Category Breakdown */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Top Products */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-bold mb-4">Top Selling Products</h3>
                    {topProducts.length > 0 ? (
                      <div className="space-y-3">
                        {topProducts.slice(0, 5).map((product, idx) => (
                          <div key={product.product_id} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-[#D4AF37] text-white' :
                                idx === 1 ? 'bg-gray-400 text-white' :
                                idx === 2 ? 'bg-amber-600 text-white' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {idx + 1}
                              </span>
                              <div>
                                <p className="font-medium text-sm">{product.product_name}</p>
                                <p className="text-xs text-gray-400">{product.quantity_sold} sold</p>
                              </div>
                            </div>
                            <p className="font-medium text-sm text-green-600">${product.revenue?.toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-[200px] text-gray-400 text-sm">
                        No sales data yet
                      </div>
                    )}
                  </div>

                  {/* Category Breakdown */}
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-bold mb-4">Sales by Category</h3>
                    {categoryBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={categoryBreakdown}
                            dataKey="revenue"
                            nameKey="category"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                            labelLine={false}
                          >
                            {categoryBreakdown.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={[
                                '#D4AF37', '#8B1F1F', '#1A1A1A', '#4A90A4', '#6B8E23', '#9B59B6'
                              ][index % 6]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
                        No sales data yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Orders by Day Chart */}
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-lg font-bold mb-4">Orders Per Day (Last 30 Days)</h3>
                  {salesChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={salesChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { day: 'numeric' })}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip 
                          formatter={(value) => [value, 'Orders']}
                          labelFormatter={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Bar dataKey="orders" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[250px] text-gray-400">
                      No orders yet
                    </div>
                  )}
                </div>

                {/* Product Stats */}
                {reportsSummary?.products && (
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-lg font-bold mb-4">Product Statistics</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-3xl font-bold">{reportsSummary.products.total}</p>
                        <p className="text-sm text-gray-500">Total Products</p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-3xl font-bold text-yellow-700">{reportsSummary.products.low_stock}</p>
                        <p className="text-sm text-yellow-600">Low Stock Items</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* US Insurance Tab */}
        {activeTab === 'usinsure' && (
          <div className="space-y-8">
            {/* Add Email Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-bold mb-4">Add Approved Email</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Single Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Single Email</label>
                  <form onSubmit={handleAddEmail} className="flex gap-2">
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="client@usinsurance.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      data-testid="add-email-input"
                    />
                    <button
                      type="submit"
                      disabled={usinsureLoading || !newEmail.trim()}
                      className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B5952F] disabled:bg-gray-300 transition-colors"
                    >
                      Add
                    </button>
                  </form>
                </div>
                
                {/* Bulk Add */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Add Emails</label>
                  <textarea
                    value={bulkEmails}
                    onChange={(e) => setBulkEmails(e.target.value)}
                    placeholder="Enter multiple emails (one per line, or comma separated)"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-sm"
                  />
                  <button
                    onClick={handleBulkAdd}
                    disabled={usinsureLoading || !bulkEmails.trim()}
                    className="mt-2 px-4 py-2 bg-[#1A1A1A] text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 transition-colors text-sm"
                  >
                    Add All Emails
                  </button>
                </div>
              </div>
            </div>

            {/* Approved Emails List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold">Approved Client Emails ({usinsureEmails.length})</h2>
                <p className="text-sm text-gray-500">These emails can request 10% discount codes</p>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {usinsureEmails.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No approved emails yet. Add emails above to get started.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {usinsureEmails.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-3 text-sm font-medium">{item.email}</td>
                          <td className="px-6 py-3 text-sm text-gray-500">
                            {new Date(item.added_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button
                              onClick={() => handleRemoveEmail(item.email)}
                              className="text-red-500 hover:text-red-700 text-sm font-medium"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Issued Codes List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-bold">Issued Discount Codes ({usinusreCodes.length})</h2>
                <p className="text-sm text-gray-500">Track all generated promo codes and their usage</p>
              </div>
              <div className="overflow-x-auto">
                {usinusreCodes.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No codes issued yet. Codes are generated when approved clients verify their email.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Used</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {usinusreCodes.map((code) => (
                        <tr key={code.id} className="hover:bg-gray-50">
                          <td className="px-6 py-3">
                            <span className="font-mono font-bold text-[#D4AF37]">{code.code}</span>
                          </td>
                          <td className="px-6 py-3 text-sm">{code.email}</td>
                          <td className="px-6 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              code.status === 'unused' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {code.status === 'unused' ? 'Available' : 'Used'}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500">
                            {new Date(code.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500">
                            {code.used_at ? new Date(code.used_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 my-auto" data-testid="add-product-modal">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button
                onClick={() => { setShowAddProduct(false); setEditingProduct(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={productForm.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Enter product name"
                    data-testid="product-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    name="category"
                    value={productForm.category}
                    onChange={(e) => {
                      setProductForm(prev => ({
                        ...prev,
                        category: e.target.value,
                        gender: '',
                        clothing_type: '',
                        traditional_type: '',
                        subcategory: ''
                      }));
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    data-testid="product-category-select"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Gender/Target Audience - Show for categories that have gender options */}
                {subcategoryOptions[productForm.category]?.gender?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {productForm.category === 'Traditional Wear' || productForm.category === 'Clothing' ? 'Gender' : 'Target Audience'}
                    </label>
                    <select
                      name="gender"
                      value={productForm.gender}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    >
                      {subcategoryOptions[productForm.category].gender.map(g => (
                        <option key={g} value={g}>
                          {g === '' ? 'All / Unisex' : g.charAt(0).toUpperCase() + g.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Subcategory/Type - Based on main category */}
                {subcategoryOptions[productForm.category]?.type?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {productForm.category === 'Clothing' ? 'Clothing Type' : 
                       productForm.category === 'Traditional Wear' ? 'Traditional Type' :
                       productForm.category === 'Jewelry' ? 'Jewelry Type' :
                       productForm.category === 'Beauty & Cosmetics' ? 'Product Type' :
                       productForm.category === 'Home & Living' ? 'Category Type' : 'Type'}
                    </label>
                    <select
                      name={productForm.category === 'Traditional Wear' ? 'traditional_type' : 'clothing_type'}
                      value={productForm.category === 'Traditional Wear' ? productForm.traditional_type : productForm.clothing_type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    >
                      {subcategoryOptions[productForm.category].type.map(t => (
                        <option key={t} value={t}>
                          {t === '' ? 'All / General' : t.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price ($) *</label>
                  <input
                    type="number"
                    name="base_price"
                    value={productForm.base_price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="0.00"
                    data-testid="product-price-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Compare at Price ($)</label>
                  <input
                    type="number"
                    name="compare_at_price"
                    value={productForm.compare_at_price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Original price for discount display"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={productForm.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                    placeholder="Enter product description"
                    data-testid="product-description-input"
                  />
                </div>

                {/* Size Guide */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Size Guide (optional)</label>
                  <textarea
                    name="size_guide"
                    value={productForm.size_guide}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-sm"
                    placeholder="Enter custom size guide info (e.g., 'S: Chest 34-36 inches, M: Chest 38-40 inches...')"
                    data-testid="product-size-guide-input"
                  />
                </div>

                {/* Care Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Care Instructions (optional)</label>
                  <textarea
                    name="care_instructions"
                    value={productForm.care_instructions}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37] text-sm"
                    placeholder="Enter care instructions (e.g., 'Machine wash cold, hang dry...')"
                    data-testid="product-care-instructions-input"
                  />
                </div>
              </div>

              {/* Images - Drag & Drop + URLs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Images</label>
                
                {/* Drag and Drop Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive 
                      ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  
                  <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-[#D4AF37]' : 'text-gray-400'}`} />
                  
                  <p className="text-sm text-gray-600 mb-2">
                    <span className="font-medium">Drag and drop</span> images here, or{' '}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="text-[#D4AF37] hover:underline font-medium"
                    >
                      browse files
                    </button>
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG, WEBP up to 10MB</p>
                  
                  {uploadingImages && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                      <div className="flex items-center gap-2 text-[#D4AF37]">
                        <div className="w-5 h-5 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
                        Uploading...
                      </div>
                    </div>
                  )}
                </div>

                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">Uploaded Images ({uploadedImages.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {uploadedImages.map((img, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={img}
                            alt={`Upload ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeUploadedImage(index)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* URL Input Section */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Or add image URLs</p>
                  <div className="space-y-2">
                    {imageUrls.map((url, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => handleImageUrlChange(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#D4AF37]"
                          placeholder="https://example.com/image.jpg"
                        />
                        {imageUrls.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeImageUrl(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addImageUrl}
                      className="text-xs text-[#D4AF37] hover:underline"
                    >
                      + Add another URL
                    </button>
                  </div>
                </div>
              </div>

              {/* Variants */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Variants (Size, Color, Stock)</label>
                <div className="space-y-3">
                  {productForm.variants.map((variant, index) => (
                    <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
                      <select
                        value={variant.size}
                        onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      >
                        {defaultSizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                      <select
                        value={variant.color}
                        onChange={(e) => handleVariantChange(index, 'color', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      >
                        {defaultColors.map(color => (
                          <option key={color} value={color}>{color}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => handleVariantChange(index, 'stock', e.target.value)}
                        min="0"
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                        placeholder="Stock"
                      />
                      <input
                        type="text"
                        value={variant.sku}
                        onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#D4AF37]"
                        placeholder="SKU (optional)"
                      />
                      {productForm.variants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeVariant(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addVariant}
                    className="text-sm text-[#D4AF37] hover:underline"
                  >
                    + Add variant
                  </button>
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="show_online"
                    checked={productForm.show_online}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm">Show Online</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_new_arrival"
                    checked={productForm.is_new_arrival}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm">New Arrival</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={productForm.is_featured}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="pickup_today"
                    checked={productForm.pickup_today}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm">Pickup Today</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_nepora_original"
                    checked={productForm.is_nepora_original}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-[#D4AF37] rounded border-gray-300 focus:ring-[#D4AF37]"
                  />
                  <span className="text-sm">Nepora Original</span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowAddProduct(false); setEditingProduct(null); }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B5952F] transition-colors disabled:opacity-50"
                  data-testid="save-product-button"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingProduct ? 'Update Product' : 'Add Product'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
